// GlobalTimerContext.js - 4cd05ae 방식 + 30초 폴링 + 정지 시 즉시 폴링
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from './UserContext';

const GlobalTimerContext = createContext(null);

export const GlobalTimerProvider = ({ children }) => {
  const { tokenUserID } = useUser();
  const queryClient = useQueryClient();
  
  // 로컬 상태 (서버 기준 + 실시간 UI 업데이트)
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false); // 🚀 중복 클릭 방지
  const isTogglingRef = useRef(false); // 🚀 ref로 최신 상태 추적 (비동기 함수에서 사용)
  const lastUpdateTimestamp = useRef(0);
  const pollingIntervalRef = useRef(null);
  const lastServerTimeRef = useRef(0); // 마지막 서버 시간 (기준점)
  const lastServerSyncRef = useRef(Date.now()); // 마지막 서버 동기화 시간
  const uiUpdateIntervalRef = useRef(null); // UI 업데이트용 (1초마다)
  const isFetchingRef = useRef(false); // 🚀 중복 호출 방지: 진행 중인 요청 추적
  
  // 🎯 타이머 데이터 가져오기 함수 (비동기 서버 동기화 - UI 블로킹 없음)
  // 🚀 서버는 단지 기준점(lastServerTimeRef)만 업데이트하고, elapsedTime은 절대 덮어쓰지 않음
  const fetchTimerRecords = useCallback(async (userId, skipElapsedTimeUpdate = false, skipIsRunningUpdate = false) => {
    if (!userId) return;
    
    // 🚀 중복 호출 방지: 진행 중인 요청이 있으면 무시
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    const requestTimestamp = Date.now();
    
    try {
      const response = await fetch(`https://api.kkokk.io/api/v2/studytimer/records?user_id=${userId}`);
      if (!response.ok) {
        throw new Error('타이머 기록을 조회하는 데 실패했습니다.');
      }
      const data = await response.json();
      
      // 오래된 응답 무시
      if (requestTimestamp < lastUpdateTimestamp.current) {
        isFetchingRef.current = false;
        return;
      }
      
      lastUpdateTimestamp.current = requestTimestamp;

      const [hours, minutes, seconds] = data.total_time.split(':').map(Number);
      const serverTotalSeconds = hours * 3600 + minutes * 60 + seconds;
      const serverIsRunning = data.is_running || false;
      
      // 🚀 서버 값으로 기준점 업데이트 (실시간 UI 업데이트의 기준)
      lastServerTimeRef.current = serverTotalSeconds;
      lastServerSyncRef.current = Date.now();
      
      // 🚀 skipElapsedTimeUpdate가 true이면 elapsedTime 덮어쓰지 않음 (깜빡임 방지)
      // 타이머가 정지되어 있거나 초기 로딩 시에만 서버 값으로 업데이트
      if (!skipElapsedTimeUpdate) {
        setElapsedTime(serverTotalSeconds); // 서버 값으로 업데이트
      }
      // skipElapsedTimeUpdate가 true이면 기준점만 업데이트하고, UI는 interval에서 계속 증가
      
      // 🚀 skipIsRunningUpdate가 true이거나 isToggling 중이면 isRunning 상태 업데이트 생략 (버튼 깜빡임 방지)
      if (!skipIsRunningUpdate && !isTogglingRef.current) {
        setIsRunning(serverIsRunning);
      }
      setIsDataLoading(false);
    } catch (error) {
      console.error('❌ [GlobalTimer] 타이머 기록 조회 중 오류 발생:', error);
      setIsDataLoading(false);
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // 🚨 tokenUserID 변경 감지 및 상태 초기화
  const prevTokenUserIDRef = useRef(null);
  useEffect(() => {
    if (tokenUserID && tokenUserID !== prevTokenUserIDRef.current) {
      const previousTokenUserID = prevTokenUserIDRef.current;
      
      // 이전 사용자의 모든 interval 정리
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (uiUpdateIntervalRef.current) {
        clearInterval(uiUpdateIntervalRef.current);
        uiUpdateIntervalRef.current = null;
      }
      
      // 이전 사용자의 모든 ref 초기화
      lastServerTimeRef.current = 0;
      lastServerSyncRef.current = Date.now();
      lastUpdateTimestamp.current = 0;
      isFetchingRef.current = false;
      isTogglingRef.current = false;
      
      // React Query 캐시 무효화
      if (previousTokenUserID) {
        queryClient.removeQueries(['timer', previousTokenUserID]);
      }
      
      // 상태 완전 초기화
      setIsRunning(false);
      setElapsedTime(0);
      setIsDataLoading(true);
      setIsToggling(false);
      
      prevTokenUserIDRef.current = tokenUserID;
    }
  }, [tokenUserID, queryClient]);

  // 타이머 토글 (중복 클릭 방지 + 즉시 상태 업데이트)
  const toggleTimer = useCallback(async () => {
    if (!tokenUserID) return;
    
    // 🚀 중복 클릭 방지
    if (isToggling) {
      console.warn('⚠️ [GlobalTimer] 타이머 토글 중복 클릭 방지');
      return;
    }
    
    setIsToggling(true);
    isTogglingRef.current = true; // 🚀 ref도 업데이트 (비동기 함수에서 사용)
    const wasRunning = isRunning;
    const action = wasRunning ? 'stop' : 'start';
    const currentElapsedTime = elapsedTime; // 현재 시간 저장 (깜빡임 방지)
    
    // 🚀 낙관적 업데이트: 즉시 로컬 상태 변경 (사용자 경험 개선)
    setIsRunning(!wasRunning);
    
    // 🚀 타이머 시작 시 즉시 시간 업데이트 초기화 (2초 지연 방지) - 4cd05ae 방식
    if (!wasRunning) {
      // 시작 시: 현재 시간을 기준점으로 설정하여 즉시 UI 업데이트 시작
      lastServerTimeRef.current = currentElapsedTime;
      lastServerSyncRef.current = Date.now();
      // elapsedTime은 그대로 유지 (00:00으로 리셋 방지)
    }
    
    try {
      const response = await fetch('https://api.kkokk.io/api/v2/studytimer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: tokenUserID, 
          action, 
          study_name: "kkokkio_studycafe" 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // 🚀 에러 발생 시 이전 상태로 롤백
        setIsRunning(wasRunning);
        
        if (errorData.message?.includes('진행 중인 타이머가 없습니다')) {
          await fetchTimerRecords(tokenUserID, false); // 서버 값으로 업데이트
          setIsToggling(false);
          return;
        }
        
        throw new Error(errorData.message || '타이머 요청 실패');
      }

      const result = await response.json();
      
      if (result.success !== false) {
        // 🚀 서버 응답 후 기준점만 업데이트 (elapsedTime, isRunning 덮어쓰지 않음 - 깜빡임 방지)
        // toggleTimer에서 이미 낙관적 업데이트를 했으므로 상태는 그대로 유지
        // 🚀 4cd05ae 방식: 서버 동기화는 백그라운드에서 비동기로 실행 (UI 블로킹 없음)
        await fetchTimerRecords(tokenUserID, !wasRunning, true); // 시작 시 skipElapsedTimeUpdate=true, skipIsRunningUpdate=true
        
        // 🚀 정지 시 백그라운드에서 서버 동기화 (UI 블로킹 없음, 즉시 버튼 활성화)
        if (wasRunning) {
          // 정지 직후 백그라운드에서 서버 동기화 (await 없이 비동기 처리)
          fetchTimerRecords(tokenUserID, false, false).then(() => {
            // 서버 동기화 완료 후 elapsedTime 최종 업데이트
            const now = Date.now();
            const timeSinceLastSync = (now - lastServerSyncRef.current) / 1000;
            const currentTime = lastServerTimeRef.current + Math.floor(timeSinceLastSync);
            setElapsedTime(currentTime);
          }).catch((error) => {
            console.warn('정지 후 서버 동기화 중 오류:', error);
          });
        }
      } else {
        // 🚀 서버에서 실패한 경우 이전 상태로 롤백
        setIsRunning(wasRunning);
      }
    } catch (error) {
      console.error('타이머 요청 중 오류 발생:', error);
      
      // 🚀 에러 발생 시 이전 상태로 롤백
      setIsRunning(wasRunning);
      
      if (error.message?.includes('진행 중인 타이머가 없습니다')) {
        try {
          await fetchTimerRecords(tokenUserID, false); // 정지 상태이므로 서버 값으로 업데이트
        } catch (syncError) {
          console.warn('동기화 중 오류:', syncError);
        }
      }
    } finally {
      setIsToggling(false);
      isTogglingRef.current = false; // 🚀 ref도 업데이트
    }
  }, [tokenUserID, isRunning, isToggling, fetchTimerRecords]);

  // 강제 동기화
  const forceSync = useCallback(async () => {
    if (!tokenUserID) return;
    
    // 🚀 타이머 실행 중이면 elapsedTime 덮어쓰지 않음 (깜빡임 방지)
    // 🚀 isToggling 중이면 isRunning 상태도 업데이트하지 않음 (버튼 깜빡임 방지)
    const skipElapsedTimeUpdate = isRunning;
    const skipIsRunningUpdate = isTogglingRef.current; // 🚀 isToggling 중이면 상태 업데이트 생략
    await fetchTimerRecords(tokenUserID, skipElapsedTimeUpdate, skipIsRunningUpdate);
    queryClient.invalidateQueries(['timer', tokenUserID]);
  }, [tokenUserID, isRunning, fetchTimerRecords, queryClient]);

  // 🚀 자동 폴링: 60초마다 서버 동기화 + 백그라운드에서는 중단
  useEffect(() => {
    if (!tokenUserID) return;
    
    // 🚀 API 병렬화: tokenUserID를 받자마자 즉시 API 호출 (UserContext와 병렬 실행)
    // UserContext의 getUserInfo와 동시에 실행되어 워터폴 방지
    fetchTimerRecords(tokenUserID, false);
    
    // 포그라운드에서만 폴링 시작
    const startPolling = () => {
      // 기존 interval 정리
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      // 60초마다 서버에서 시간 가져오기 (30초 → 60초로 증가)
      pollingIntervalRef.current = setInterval(() => {
        if (!document.hidden) { // 포그라운드에서만 실행
          const skipUpdate = isRunning; // 실행 중이면 시간 덮어쓰지 않음
          fetchTimerRecords(tokenUserID, skipUpdate);
        }
      }, 60000); // 60초마다
    };
    
    // 백그라운드에서는 폴링 중단
    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    
    // 초기 폴링 시작
    startPolling();
    
    // visibilitychange 이벤트로 백그라운드/포그라운드 전환 감지
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 백그라운드로 전환 시 폴링 중단
        stopPolling();
      } else {
        // 포그라운드 복귀 시 즉시 동기화 후 폴링 재개
        fetchTimerRecords(tokenUserID, isRunning);
        startPolling();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [tokenUserID, isRunning, fetchTimerRecords]);

  // 🚀 실시간 UI 업데이트: 1초마다 (서버 시간 기준으로 계산 - 서버 동기화와 독립 실행)
  useEffect(() => {
    if (!isRunning) {
      // 타이머가 정지되어 있으면 UI 업데이트 불필요
      if (uiUpdateIntervalRef.current) {
        clearInterval(uiUpdateIntervalRef.current);
        uiUpdateIntervalRef.current = null;
      }
      return;
    }

    // 🚀 타이머 시작 시 즉시 기준점 초기화 (2초 지연 방지) - 4cd05ae 방식
    // lastServerTimeRef가 0이거나 오래된 값이면 현재 elapsedTime으로 초기화
    if (lastServerTimeRef.current === 0 || lastServerSyncRef.current === 0) {
      lastServerTimeRef.current = elapsedTime;
      lastServerSyncRef.current = Date.now();
    }

    // 🚀 UI 업데이트 함수 (중복 실행 방지)
    const updateTime = () => {
      const now = Date.now();
      const timeSinceLastSync = (now - lastServerSyncRef.current) / 1000; // 초 단위
      const currentTime = lastServerTimeRef.current + Math.floor(timeSinceLastSync);
      setElapsedTime(currentTime);
    };
    
    // 즉시 한 번 실행 (지연 없이 시간 표시)
    updateTime();

    // 🚀 타이머가 실행 중이면 1초마다 UI 업데이트 (서버 동기화와 독립적으로 실행)
    uiUpdateIntervalRef.current = setInterval(updateTime, 1000); // 1초마다

    return () => {
      if (uiUpdateIntervalRef.current) {
        clearInterval(uiUpdateIntervalRef.current);
        uiUpdateIntervalRef.current = null;
      }
    };
  }, [isRunning, elapsedTime]); // 🚀 4cd05ae 방식: elapsedTime 의존성 유지


  // 시간 포맷팅 유틸리티
  const formatTime = useCallback((seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }, []);

  // 🚀 Context value 메모이제이션 (함수들은 useCallback으로 안정화되어 있음)
  const value = useMemo(() => ({
    isRunning,
    elapsedTime,
    isDataLoading,
    isToggling,
    tokenUserID,
    toggleTimer,
    forceSync,
    formatTime,
    fetchTimerRecords
  }), [
    isRunning,
    elapsedTime,
    isDataLoading,
    isToggling,
    tokenUserID,
    toggleTimer,
    forceSync,
    formatTime,
    fetchTimerRecords
  ]);

  return (
    <GlobalTimerContext.Provider value={value}>
      {children}
    </GlobalTimerContext.Provider>
  );
};

export const useGlobalTimer = () => {
  const context = useContext(GlobalTimerContext);
  if (!context) {
    throw new Error('useGlobalTimer must be used within a GlobalTimerProvider');
  }
  return context;
};

export default GlobalTimerContext;
